import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdEmail } from 'react-icons/md';
import { RiLockPasswordLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import { FiArrowRight } from 'react-icons/fi';
import useRequest from '../../hooks/useRequest';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
  });
  const [isHovered, setIsHovered] = useState(false);
  const { loading, sendRequest } = useRequest();
  const { login, auth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/dashboard/group-list');
    }
  }, [auth.isAuthenticated, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value.trim()
    }));
    
    setValidationErrors(prev => ({
      ...prev,
      [id]: ''
    }));
  };

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: '',
    };
    let isValid = true;

    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 1) {
      newErrors.password = 'Password cannot be empty';
      isValid = false;
    }

    setValidationErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setValidationErrors({
      email: '',
      password: '',
    });

    if (!validateForm()) {
      return;
    }

    try {
      const response = await sendRequest(
        'POST',
        '/customer/login/',
        formData
      );
      
      // Save user data to localStorage
      const userData = {
        id: response.id,
        email: response.email,
        name: response.name,
        refresh_token: response.refresh_token,
        company: response.company
      };
      localStorage.setItem('userData', JSON.stringify(userData));
      
      login(response.token, response.refresh_token);
      navigate('/dashboard/broker');
    } catch (err) {
      console.error('Login failed:', err);
      if (err.statusCode === 401) {
        setValidationErrors(prev => ({
          ...prev,
          password: 'Invalid email or password'
        }));
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/5 to-[#543DF4]/5" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#C334F6]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[#543DF4]/10 rounded-full blur-3xl animate-float-reverse" />
      </div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="min-h-screen flex items-center justify-center px-4 py-12"
        >
          <motion.div 
            className="w-full max-w-[480px] bg-white/5 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/10 relative overflow-hidden"
            variants={itemVariants}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C334F6] to-[#543DF4]"></div>
            <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-gradient-to-br from-[#C334F6]/10 to-[#543DF4]/10"></div>
            <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-gradient-to-br from-[#C334F6]/10 to-[#543DF4]/10 blur-2xl"></div>
            <div className="absolute -top-20 -left-20 w-40 h-40 rounded-full bg-gradient-to-br from-[#543DF4]/10 to-[#C334F6]/10 blur-2xl"></div>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="flex justify-center mb-8"
            >
              <span className="inline-block px-6 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-white">
                Welcome Back
              </span>
            </motion.div>

            <motion.h1 
              className="text-4xl font-normal leading-[1.2] tracking-[0.001em] text-center text-white mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Sign in to your
              <br />
              <span className="bg-gradient-to-r from-[#C334F6] to-[#543DF4] bg-clip-text text-transparent">
                Account
              </span>
            </motion.h1>
            
            <motion.form 
              className="space-y-6 relative z-10" 
              onSubmit={handleSubmit} 
              noValidate
              variants={itemVariants}
            >
              <motion.div 
                className="relative"
                variants={itemVariants}
              >
                <label htmlFor="email" className="block text-sm font-normal text-white/80 mb-2">
                  Email Address *
                </label>
                <div className="relative group">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" 
                      style={{background: 'linear-gradient(180deg, rgba(195, 52, 246, 0.1) 0%, rgba(84, 61, 244, 0.1) 100%)'}}>
                      <MdEmail className="text-white/60 transition-colors duration-300 group-hover:text-[#C334F6]" size={14} />
                    </div>
                  </span>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full h-[44px] pl-12 pr-4 py-3 rounded-full bg-white/5 border text-white font-medium text-sm placeholder-white/40 ${
                      validationErrors.email 
                        ? 'border-red-500 focus:border-red-500' 
                        : 'border-white/10 focus:border-[#C334F6]'
                    } focus:ring-2 focus:ring-[#C334F6]/20 transition-all duration-300`}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <AnimatePresence>
                  {validationErrors.email && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="text-red-400 text-sm mt-1 ml-3"
                    >
                      {validationErrors.email}
                    </motion.p>
                  )}
                </AnimatePresence>
              </motion.div>
              
              <motion.div 
                className="relative"
                variants={itemVariants}
              >
                <label htmlFor="password" className="block text-sm font-normal text-white/80 mb-2">
                  Password *
                </label>
                <div className="relative group">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" 
                      style={{background: 'linear-gradient(180deg, rgba(195, 52, 246, 0.1) 0%, rgba(84, 61, 244, 0.1) 100%)'}}>
                      <RiLockPasswordLine className="text-white/60 transition-colors duration-300 group-hover:text-[#C334F6]" size={14} />
                    </div>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full h-[44px] pl-12 pr-12 py-3 rounded-full bg-white/5 border text-white font-medium text-sm placeholder-white/40 ${
                      validationErrors.password 
                        ? 'border-red-500 focus:border-red-500' 
                        : 'border-white/10 focus:border-[#C334F6]'
                    } focus:ring-2 focus:ring-[#C334F6]/20 transition-all duration-300`}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-[#C334F6] transition-colors duration-300"
                  >
                    {showPassword ? <RiEyeOffLine size={20} /> : <RiEyeLine size={20} />}
                  </button>
                </div>
                <AnimatePresence>
                  {validationErrors.password && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="text-red-400 text-sm mt-1 ml-3"
                    >
                      {validationErrors.password}
                    </motion.p>
                  )}
                </AnimatePresence>
              </motion.div>
              
              <motion.button
                type="submit"
                disabled={loading}
                className="w-full h-[41px] px-6 py-3 rounded-full bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white font-medium flex items-center justify-center disabled:opacity-70 relative overflow-hidden group"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 flex items-center">
                  {loading ? 'Logging in...' : 'Login'}
                  <motion.span 
                    className="ml-2 bg-white rounded-full p-0.5"
                    animate={{ x: isHovered ? 5 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FiArrowRight className="text-black" size={14} />
                  </motion.span>
                </span>
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-[#543DF4] to-[#C334F6] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={false}
                  animate={{ opacity: isHovered ? 1 : 0 }}
                />
              </motion.button>
            </motion.form>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default Login;